/**
 * Хуанли — өдөр бүрийн борлуулалт, зарагдсан болон агуулахад орж ирсэн гутал.
 * Өдөр дээр дарахад тэр өдрийн бүх үйл явдал нээгдэнэ (`DayDetail`).
 *
 * Сар (`?month=2026-09`) ба сонгосон өдөр (`?date=2026-09-28`) URL-д —
 * хуудас сэргээхэд алдагдахгүй, холбоосоор хуваалцана.
 *
 * ⚠️ Тоонуудын тодорхойлолт `@boots/domain` → `calendar.ts`. Борлуулалт нь
 *    кассын өдрийн хаалттай ИЖИЛ (`summarizeDay`): хожим буцаагдсан
 *    гутал тэр өдрөөс хасагдахгүй, буцаалт буцаасан өдөртөө харагдана.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  calendarDayRange, calendarMonthRange, formatMntShort, formatQty, periodBounds, shiftMonth,
  ulaanbaatarDateKey, type CalendarMonth,
} from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { Section } from '../components/section';
import { StatCard, StatGrid } from '../components/stat-card';
import { ErrorState } from '../components/empty-state';
import { SkeletonStatGrid, Skeleton } from '../components/skeleton';
import { MonthGrid } from '../components/calendar/month-grid';
import { DayDetail } from '../components/calendar/day-detail';

const NAV =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-hair ' +
  'bg-panel px-3 text-meta text-ink-2 transition hover:bg-accent-100 hover:text-ink ' +
  'disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-8 sm:min-w-8';

export function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const today = ulaanbaatarDateKey(new Date());
  const thisMonth = today.slice(0, 7);

  const rawMonth = params.get('month') ?? '';
  const month = calendarMonthRange(rawMonth) ? rawMonth : thisMonth;
  const rawDate = params.get('date') ?? '';
  const date = calendarDayRange(rawDate) ? rawDate : null;

  const set = (patch: { month?: string; date?: string | null }) => {
    const next = new URLSearchParams(params);
    if (patch.month !== undefined) {
      if (patch.month === thisMonth) next.delete('month');
      else next.set('month', patch.month);
    }
    if (patch.date !== undefined) {
      if (patch.date) next.set('date', patch.date);
      else next.delete('date');
    }
    setParams(next, { replace: true });
  };

  // Өдрөөр гүйлгэхэд сар солигдвол нүд ч дагана
  const pickDay = (d: string | null) => set(d ? { date: d, month: d.slice(0, 7) } : { date: null });

  const q = useQuery({
    queryKey: ['calendar', month],
    queryFn: () => api<Envelope<CalendarMonth>>(`/reports/calendar?month=${month}`),
    placeholderData: keepPreviousData,
  });
  const data = q.data?.data.month === month ? q.data.data : undefined;

  return (
    <div className="flex flex-col gap-3">
      <Section
        title={periodBounds('month', month).label}
        subtitle="Өдөр дээр дарж дэлгэрэнгүйг харна"
        actions={
          <div className="flex items-center gap-1.5">
            <button type="button" className={NAV} aria-label="Өмнөх сар" onClick={() => set({ month: shiftMonth(month, -1) })}>
              ‹
            </button>
            <button type="button" className={NAV} disabled={month === thisMonth} onClick={() => set({ month: thisMonth })}>
              Энэ сар
            </button>
            <button
              type="button"
              className={NAV}
              aria-label="Дараагийн сар"
              disabled={month >= thisMonth}
              onClick={() => set({ month: shiftMonth(month, 1) })}
            >
              ›
            </button>
          </div>
        }
      >
        {q.error ? (
          <ErrorState message={(q.error as Error).message} onRetry={() => void q.refetch()} />
        ) : !data ? (
          <>
            <SkeletonStatGrid />
            <Skeleton className="h-[420px] w-full" />
          </>
        ) : (
          <>
            <MonthTotals totals={data.totals} />
            <MonthGrid days={data.days} today={today} selected={date} onPick={pickDay} />
          </>
        )}
      </Section>

      {date ? <DayDetail date={date} today={today} onPick={pickDay} /> : null}
    </div>
  );
}

function MonthTotals({ totals: t }: { totals: CalendarMonth['totals'] }) {
  return (
    <StatGrid>
      <StatCard
        icon="coins"
        // «Орлого» БИШ (2026-09-29): Тайлангийн «Орлого» нь хожим буцаагдсаныг
        // хассан тул ижил сард өөр тоо гарна — нэр нь ялгаатай байх ёстой.
        label="Сарын борлуулалт"
        value={formatMntShort(t.salesMnt)}
        delta={{ text: `${formatQty(t.orders)} баримт` }}
        info={
          <>
            Баримтуудын цэвэр борлуулалт — хөнгөлөлт хассан, Зээл, И-баримтын
            нэмэгдэлгүй. Кассын өдрийн тайлантай ижил. Буцаалт нь буцаасан
            өдөртөө тусдаа харагдана — тиймээс Тайлангийн «Орлого»-оос
            (хожим буцаагдсаныг хассан) ялгаатай байж болно.
          </>
        }
      />
      <StatCard
        icon="receipt"
        label="Зарагдсан гутал"
        value={`${formatQty(t.units)} ш`}
        {...(t.returns > 0 ? { delta: { text: `${formatQty(t.returns)} буцаалт` } } : {})}
      />
      <StatCard
        icon="warehouse"
        label="Агуулахад орж ирсэн"
        value={`${formatQty(t.warehouseIn)} ш`}
        info="Оросоос ирж агуулахад хүлээн авсан гутал. Салбараас буцаж ирсэн шилжүүлэг орохгүй."
      />
      <StatCard
        icon="box"
        label="Шинээр бүртгэгдсэн"
        value={`${formatQty(t.registered)} ш`}
        info="Системд шинээр үүссэн гутал — аяллаас бүртгэсэн, эхний үлдэгдэл."
      />
    </StatGrid>
  );
}
